import { createFileRoute, redirect, useParams, useRouter, useSearch } from '@tanstack/react-router';
import {
  queryKeys,
  useGetIcpProductCategoryQuery,
  useGetImageClassificationQuery,
  useGetProductCategoryQuery,
} from '@vizit/api';
import { FEATURE_FLAGS } from '@vizit/constants';
import { getFeatureFlag } from '@vizit/feature-flags';
import * as React from 'react';
import { ExploreClassification } from '../../../../../pages';
import { PagesProvider } from '../../../../../pages/providers/pages-provider';
import { EXPLORE_BREADCRUMB, beforeLoadAuthenticatedValidation } from '../../../../../router/route-utils';
import { NavbarPageTemplate } from '../../../../../templates';

export const Route = createFileRoute(
  '/_authenticated/w/$workspaceSlug/explore/$categoryL1_/$categoryL4_/$classification',
)({
  staticData: {
    pageName: 'Explore Classification',
  },
  beforeLoad: async ({ context, location, params }) => {
    await beforeLoadAuthenticatedValidation(context, location);

    const isClipExperimentEnabled = await getFeatureFlag(FEATURE_FLAGS.TMP_CLIP_EXPERIMENT, false);
    if (isClipExperimentEnabled) {
      throw redirect({ to: '/w/$workspaceSlug/', params: { workspaceSlug: params.workspaceSlug } });
    }
  },
  validateSearch: (search: Record<string, unknown>): { exploreImageId?: string } => {
    return {
      exploreImageId: typeof search.exploreImageId === 'string' ? search.exploreImageId : undefined,
    };
  },
  loader: async ({ params, context }) => {
    const l1Category = await context.queryClient.ensureQueryData({
      queryKey: queryKeys.categories.icpProduct({ icp_product_category_id: params.categoryL1 }).queryKey,
      queryFn: () => useGetIcpProductCategoryQuery.fetcher({ icp_product_category_id: params.categoryL1 })(),
    });

    const l4Category = await context.queryClient.ensureQueryData({
      queryKey: queryKeys.categories.product({ category_id: params.categoryL4 }).queryKey,
      queryFn: () => useGetProductCategoryQuery.fetcher({ category_id: params.categoryL4 })(),
    });

    const classification = await context.queryClient.ensureQueryData({
      queryKey: queryKeys.categories.imageClassification({ classification_id: params.classification }).queryKey,
      queryFn: () => useGetImageClassificationQuery.fetcher({ classification_id: params.classification })(),
    });

    return {
      breadcrumbs: [
        EXPLORE_BREADCRUMB,
        {
          label: l1Category?.category_name ?? 'Category L1',
          href: `/w/${params.workspaceSlug}/explore/${params.categoryL1}`,
        },
        {
          label: l4Category?.name ?? 'Category L4',
          href: `/w/${params.workspaceSlug}/explore/${params.categoryL1}/${params.categoryL4}`,
        },
        { label: classification?.label ?? 'Classification' },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { categoryL1, categoryL4, classification, workspaceSlug } = useParams({ from: Route.id });
  const router = useRouter();
  const search = useSearch({ from: Route.id });

  const handleAssetClick = React.useCallback(
    (imageId: string) => {
      router.navigate({
        to: '/w/$workspaceSlug/explore/$categoryL1/$categoryL4/$classification',
        params: { workspaceSlug, categoryL1, categoryL4, classification },
        resetScroll: false,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          exploreImageId: imageId,
        }),
      });
    },
    [router, categoryL1, categoryL4, classification, workspaceSlug],
  );

  const handleCloseModal = React.useCallback(() => {
    router.navigate({
      to: '/w/$workspaceSlug/explore/$categoryL1/$categoryL4/$classification',
      params: { workspaceSlug, categoryL1, categoryL4, classification },
      resetScroll: false,
      search: (prev: Record<string, unknown>) => {
        const { exploreImageId, ...rest } = prev;
        return rest;
      },
    });
  }, [router, categoryL1, categoryL4, classification, workspaceSlug]);

  return (
    <PagesProvider template={NavbarPageTemplate}>
      <ExploreClassification
        categoryIcp={categoryL1}
        categoryL4={categoryL4}
        classification={classification}
        exploreImageId={search.exploreImageId}
        onAssetClick={handleAssetClick}
        onCloseModal={handleCloseModal}
      />
    </PagesProvider>
  );
}
